const SCROLL_LOCK_COUNT_KEY = 'siteModalLockCount';
const SCROLL_LOCK_Y_KEY = 'siteModalScrollY';
const SCROLL_LOCK_PREV_POSITION_KEY = 'siteModalPrevPosition';
const SCROLL_LOCK_PREV_TOP_KEY = 'siteModalPrevTop';
const SCROLL_LOCK_PREV_LEFT_KEY = 'siteModalPrevLeft';
const SCROLL_LOCK_PREV_RIGHT_KEY = 'siteModalPrevRight';
const SCROLL_LOCK_PREV_WIDTH_KEY = 'siteModalPrevWidth';

const normalizePath = (urlLike) => {
  try {
    const parsed = new URL(urlLike, window.location.origin);
    let pathname = parsed.pathname || '/';

    if (pathname.length > 1 && pathname.endsWith('/')) {
      pathname = pathname.slice(0, -1);
    }

    return pathname || '/';
  } catch (error) {
    return null;
  }
};

const lockPageScroll = () => {
  const body = document.body;
  const lockCount = Number(body.dataset[SCROLL_LOCK_COUNT_KEY] || '0');

  if (lockCount === 0) {
    const scrollY = window.scrollY || window.pageYOffset || 0;

    body.dataset[SCROLL_LOCK_Y_KEY] = String(scrollY);
    body.dataset[SCROLL_LOCK_PREV_POSITION_KEY] = body.style.position || '';
    body.dataset[SCROLL_LOCK_PREV_TOP_KEY] = body.style.top || '';
    body.dataset[SCROLL_LOCK_PREV_LEFT_KEY] = body.style.left || '';
    body.dataset[SCROLL_LOCK_PREV_RIGHT_KEY] = body.style.right || '';
    body.dataset[SCROLL_LOCK_PREV_WIDTH_KEY] = body.style.width || '';

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.classList.add('site-modal-open');
  }

  body.dataset[SCROLL_LOCK_COUNT_KEY] = String(lockCount + 1);
};

const unlockPageScroll = () => {
  const body = document.body;
  const lockCount = Number(body.dataset[SCROLL_LOCK_COUNT_KEY] || '0');

  if (lockCount <= 1) {
    const scrollY = Number(body.dataset[SCROLL_LOCK_Y_KEY] || '0');

    body.style.position = body.dataset[SCROLL_LOCK_PREV_POSITION_KEY] || '';
    body.style.top = body.dataset[SCROLL_LOCK_PREV_TOP_KEY] || '';
    body.style.left = body.dataset[SCROLL_LOCK_PREV_LEFT_KEY] || '';
    body.style.right = body.dataset[SCROLL_LOCK_PREV_RIGHT_KEY] || '';
    body.style.width = body.dataset[SCROLL_LOCK_PREV_WIDTH_KEY] || '';
    body.classList.remove('site-modal-open');

    delete body.dataset[SCROLL_LOCK_COUNT_KEY];
    delete body.dataset[SCROLL_LOCK_Y_KEY];
    delete body.dataset[SCROLL_LOCK_PREV_POSITION_KEY];
    delete body.dataset[SCROLL_LOCK_PREV_TOP_KEY];
    delete body.dataset[SCROLL_LOCK_PREV_LEFT_KEY];
    delete body.dataset[SCROLL_LOCK_PREV_RIGHT_KEY];
    delete body.dataset[SCROLL_LOCK_PREV_WIDTH_KEY];

    window.scrollTo(0, scrollY);
    return;
  }

  body.dataset[SCROLL_LOCK_COUNT_KEY] = String(lockCount - 1);
};

const initSiteModals = () => {
  const modalNodes = Array.from(document.querySelectorAll('[data-site-modal]'));

  if (!modalNodes.length) {
    return;
  }

  const body = document.body;
  const isStandalone = body.classList.contains('modal-standalone-page');
  const modalsByPath = new Map();

  modalNodes.forEach((modal) => {
    const path = normalizePath(modal.dataset.modalPath || '');
    if (path) {
      modalsByPath.set(path, modal);
    }
  });

  let activeModal = modalNodes.find((modal) => modal.classList.contains('is-open')) || null;
  let previousPath = normalizePath(window.location.pathname) || '/';

  const setModalState = (modal, isOpen) => {
    modal.classList.toggle('is-open', isOpen);
    modal.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
  };

  const closeModal = ({ restorePath = true } = {}) => {
    if (!activeModal) {
      return;
    }

    setModalState(activeModal, false);
    unlockPageScroll();

    if (!isStandalone && restorePath && previousPath && normalizePath(window.location.pathname) !== previousPath) {
      window.history.pushState({ siteModal: false }, '', previousPath);
    }

    activeModal = null;
  };

  const openModal = (modal, { pushUrl = true } = {}) => {
    if (!modal) {
      return;
    }

    if (activeModal && activeModal !== modal) {
      setModalState(activeModal, false);
      unlockPageScroll();
    }

    const modalPath = normalizePath(modal.dataset.modalPath || '');

    if (!activeModal) {
      previousPath = normalizePath(window.location.pathname) || '/';
    }

    activeModal = modal;
    setModalState(modal, true);
    lockPageScroll();

    if (!isStandalone && pushUrl && modalPath && normalizePath(window.location.pathname) !== modalPath) {
      window.history.pushState({ siteModal: true, path: modalPath }, '', modalPath);
    }

    const firstFocusable = modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    firstFocusable?.focus();
  };

  const maybeOpenForCurrentPath = ({ pushUrl = false } = {}) => {
    const currentPath = normalizePath(window.location.pathname);
    const matchedModal = currentPath ? modalsByPath.get(currentPath) : null;

    if (matchedModal) {
      openModal(matchedModal, { pushUrl });
      return true;
    }

    return false;
  };

  modalNodes.forEach((modal) => {
    const closeControls = modal.querySelectorAll('[data-site-modal-close]');

    closeControls.forEach((control) => {
      control.addEventListener('click', (event) => {
        event.preventDefault();

        if (isStandalone) {
          const fallbackHref = control.getAttribute('href') || '/';
          window.location.assign(fallbackHref);
          return;
        }

        closeModal({ restorePath: true });
      });
    });
  });

  document.addEventListener('click', (event) => {
    const anchor = event.target.closest('a[href]');

    if (!anchor) {
      return;
    }

    if (anchor.hasAttribute('download')) {
      return;
    }

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }

    const target = (anchor.getAttribute('target') || '').toLowerCase();
    if (target && target !== '_self') {
      return;
    }

    let destination;
    try {
      destination = new URL(anchor.getAttribute('href'), window.location.href);
    } catch (error) {
      return;
    }

    if (destination.origin !== window.location.origin) {
      return;
    }

    const destinationPath = normalizePath(destination.pathname);
    const matchedModal = destinationPath ? modalsByPath.get(destinationPath) : null;

    if (!matchedModal) {
      return;
    }

    event.preventDefault();
    openModal(matchedModal, { pushUrl: true });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') {
      return;
    }

    if (isStandalone) {
      return;
    }

    closeModal({ restorePath: true });
  });

  window.addEventListener('popstate', () => {
    if (isStandalone) {
      return;
    }

    if (maybeOpenForCurrentPath({ pushUrl: false })) {
      return;
    }

    if (activeModal) {
      closeModal({ restorePath: false });
    }
  });

  if (isStandalone) {
    const standaloneModal = activeModal || modalNodes[0];
    if (standaloneModal) {
      openModal(standaloneModal, { pushUrl: false });
    }
    return;
  }

  maybeOpenForCurrentPath({ pushUrl: false });
};

document.addEventListener('DOMContentLoaded', () => {
  initSiteModals();
});
